import React, { useEffect, useState, useRef } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { ShoppingBag, User, Menu, Search } from 'lucide-react';
import { Logo } from './Logo';
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from './ui/Sheet';
import { useCart } from './cart/CartContext';
import { SearchPopover } from './SearchPopover';
type NavItem = {
  label: string;
  to: string;
  isRoute?: boolean;
};
const NAV: NavItem[] = [{
  label: 'Shop',
  to: '/shop',
  isRoute: true
}, {
  label: 'Bundles',
  to: '/bundles',
  isRoute: true
}, {
  label: 'Articles',
  to: '/articles',
  isRoute: true
}, {
  label: 'About',
  to: '/about',
  isRoute: true
}, {
  label: 'FAQ',
  to: '/faq',
  isRoute: true
}];
export function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const {
    totalCount,
    openDrawer
  } = useCart();
  const searchTriggerRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll, {
      passive: true
    });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  // Cmd+K / Ctrl+K to toggle search
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setSearchOpen(s => !s);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);
  return <header className={`sticky top-0 z-40 w-full backdrop-blur-md border-b transition-[background-color,border-color,box-shadow] duration-200 ${scrolled ? 'bg-background/90 border-border [box-shadow:0_1px_0_0_rgba(15,20,16,0.04),0_4px_12px_-6px_rgba(15,20,16,0.10)]' : 'bg-background/85 border-border/60'}`} data-id="element-252">
      <div className="container mx-auto flex items-center justify-between h-16" data-id="element-253">
        {/* Left: Logo + Nav */}
        <div className="flex items-center gap-10" data-id="element-254">
          <Logo data-id="element-255" />
          <nav className="hidden lg:flex items-center gap-7 h-full" data-id="element-256">
            {NAV.map(item => <NavLink key={item.to} to={item.to} end data-id="element-257">
                {({
              isActive
            }) => <span className={`relative h-16 inline-flex items-center text-[14px] tracking-[-0.005em] transition-colors ${isActive ? 'text-foreground font-semibold' : 'text-muted-foreground hover:text-foreground font-medium'}`} data-id="element-258">
                    {item.label}
                    {isActive && <span className="absolute left-0 right-0 bottom-0 h-[2.5px] bg-primary rounded-t-full" aria-hidden data-id="element-259" />}
                  </span>}
              </NavLink>)}
          </nav>
        </div>

        {/* Right: actions */}
        <div className="flex items-center gap-1" data-id="element-260">
          {/* Search trigger + popover anchor */}
          <div className="relative" data-id="element-261">
            <button ref={searchTriggerRef} onClick={() => setSearchOpen(s => !s)} aria-label="Search peptides" aria-expanded={searchOpen} className={`hidden md:inline-flex items-center justify-center h-9 w-9 rounded-full transition-colors ${searchOpen ? 'bg-secondary text-foreground' : 'text-muted-foreground hover:text-foreground hover:bg-secondary/60'}`} data-id="element-262">
              <Search className="h-[17px] w-[17px]" strokeWidth={1.75} data-id="element-263" />
            </button>
            <SearchPopover open={searchOpen} onClose={() => setSearchOpen(false)} triggerRef={searchTriggerRef} data-id="element-264" />
          </div>

          {/* Account */}
          <button aria-label="Account" className="hidden md:inline-flex items-center justify-center h-9 w-9 rounded-full text-muted-foreground hover:text-foreground hover:bg-secondary/60 transition-colors" data-id="element-265">
            <User className="h-[17px] w-[17px]" strokeWidth={1.75} data-id="element-266" />
          </button>

          {/* Cart */}
          <button aria-label={`Cart (${totalCount} ${totalCount === 1 ? 'item' : 'items'})`} onClick={openDrawer} className="relative inline-flex items-center justify-center h-9 w-9 rounded-full text-muted-foreground hover:text-foreground hover:bg-secondary/60 transition-colors" data-id="element-267">
            <ShoppingBag className="h-[17px] w-[17px]" strokeWidth={1.75} data-id="element-268" />
            {totalCount > 0 && <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-primary text-[10px] font-mono font-semibold text-primary-foreground flex items-center justify-center [font-feature-settings:'tnum']" data-id="element-269">
                {totalCount}
              </span>}
          </button>

          {/* Mobile menu */}
          <Sheet data-id="element-270">
            <SheetTrigger asChild data-id="element-271">
              <button aria-label="Menu" className="lg:hidden inline-flex items-center justify-center h-9 w-9 rounded-full text-foreground hover:bg-secondary/60 transition-colors" data-id="element-272">
                <Menu className="h-5 w-5" strokeWidth={1.75} data-id="element-273" />
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[320px]" data-id="element-274">
              <SheetTitle className="sr-only" data-id="element-275">Menu</SheetTitle>
              <div className="flex flex-col gap-7 mt-6 h-full" data-id="element-276">
                <Logo data-id="element-277" />
                <nav className="flex flex-col gap-1" data-id="element-278">
                  {NAV.map(item => <NavLink key={item.to} to={item.to} end data-id="element-279">
                      {({
                    isActive
                  }) => <span className={`relative block text-[15px] tracking-[-0.005em] py-3 pl-4 pr-3 rounded-lg transition-colors ${isActive ? 'text-foreground font-semibold bg-secondary/50' : 'text-muted-foreground hover:text-foreground hover:bg-secondary/30 font-medium'}`} data-id="element-280">
                          {isActive && <span className="absolute left-0 top-2 bottom-2 w-[3px] bg-primary rounded-full" aria-hidden data-id="element-281" />}
                          {item.label}
                        </span>}
                    </NavLink>)}
                </nav>

                <div className="mt-auto pt-6 border-t border-border space-y-1" data-id="element-282">
                  <button className="flex items-center gap-3 w-full text-[14px] py-2.5 px-3 -mx-3 rounded-lg text-muted-foreground hover:text-foreground hover:bg-secondary/30 transition-colors" data-id="element-283">
                    <Search className="h-4 w-4" strokeWidth={1.75} data-id="element-284" />
                    Search peptides
                  </button>
                  <button className="flex items-center gap-3 w-full text-[14px] py-2.5 px-3 -mx-3 rounded-lg text-muted-foreground hover:text-foreground hover:bg-secondary/30 transition-colors" data-id="element-285">
                    <User className="h-4 w-4" strokeWidth={1.75} data-id="element-286" />
                    Account
                  </button>
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>;
}